import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Chip, Paper, Typography } from "@material-ui/core";
import Timeline from "@material-ui/lab/Timeline";
import TimelineConnector from "@material-ui/lab/TimelineConnector";
import TimelineContent from "@material-ui/lab/TimelineContent";
import TimelineDot from "@material-ui/lab/TimelineDot";
import TimelineItem from "@material-ui/lab/TimelineItem";
import TimelineSeparator from "@material-ui/lab/TimelineSeparator";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import { useTranslate } from "react-admin";

const useStyles = makeStyles((theme) => ({
  panel: {
    height: "100%",
    padding: theme.spacing(2),
    borderRadius: 8,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    boxShadow: "none",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
  headerIcon: {
    color: theme.palette.primary.main,
    fontSize: 20,
  },
  title: {
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 0,
  },
  accountSummary: {
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 8,
    backgroundColor: theme.palette.action.hover,
    marginBottom: theme.spacing(2),
  },
  accountCode: {
    fontWeight: 700,
    lineHeight: 1.3,
  },
  accountName: {
    marginTop: 2,
  },
  breadcrumb: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(2),
  },
  breadcrumbText: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  timeline: {
    padding: 0,
    margin: 0,
    "& .MuiTimelineItem-missingOppositeContent:before": {
      flex: 0,
      padding: 0,
    },
  },
  timelineItem: {
    minHeight: 58,
  },
  connector: {
    backgroundColor: theme.palette.divider,
    minHeight: 22,
  },
  timelineContent: {
    paddingTop: 0,
    paddingBottom: theme.spacing(1.5),
    paddingRight: 0,
  },
  levelLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: theme.palette.text.secondary,
  },
  levelCode: {
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.3,
    color: theme.palette.text.primary,
  },
  levelName: {
    fontSize: 13,
    fontWeight: 600,
    lineHeight: 1.35,
    color: theme.palette.text.primary,
  },
  currentLevel: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.25, 1.5),
    borderRadius: 8,
    border: `1px solid ${theme.palette.primary.main}`,
    backgroundColor: theme.palette.action.hover,
  },
}));

const DEFAULT_GROUP_PATH = [
  { level: 1, code: "1000", name: "สินทรัพย์ (Assets)" },
  { level: 2, code: "1100", name: "สินทรัพย์หมุนเวียน" },
  { level: 3, code: "1110", name: "เงินสดและเงินฝากธนาคาร" },
  { level: 4, code: "1111", name: "เงินสดในมือและเงินสดย่อย" },
];

const normalizePath = (path) => {
  if (!path || !path.length) {
    return DEFAULT_GROUP_PATH;
  }

  return DEFAULT_GROUP_PATH.map((fallbackNode, index) => {
    const pathNode = path[index];
    if (typeof pathNode === "string") {
      return { ...fallbackNode, name: pathNode };
    }
    if (pathNode && typeof pathNode === "object") {
      return { ...fallbackNode, ...pathNode };
    }
    return fallbackNode;
  });
};

const AccountCodeGroupingPathMockup = (props) => {
  const { accountCode, accountDescription, accountDescription2, path } = props;
  const classes = useStyles();
  const translate = useTranslate();
  const groupPath = normalizePath(path);
  const currentGroup = groupPath[groupPath.length - 1];

  return (
    <Paper variant="outlined" className={classes.panel}>
      <div className={classes.header}>
        <AccountTreeIcon className={classes.headerIcon} />
        <Typography className={classes.title} color="textPrimary">
          {translate("ra.module.Account Code Grouping Path")}
        </Typography>
      </div>

      <div className={classes.accountSummary}>
        <Typography variant="caption" color="textSecondary">
          {translate("ra.fieldAbbr.accountCode")}
        </Typography>
        <Typography className={classes.accountCode} variant="subtitle1" color="textPrimary">
          {accountCode || "1010000"}
        </Typography>
        <Typography className={classes.accountName} variant="body2" color="textSecondary">
          {accountDescription || accountDescription2 || "***CASH ON HAND-HOUSE FUND***"}
        </Typography>
      </div>

      <div className={classes.breadcrumb}>
        {groupPath.map((node, index) => (
          <React.Fragment key={`${node.level}-${node.code}`}>
            <Chip size="small" variant="outlined" label={`${node.code} ${node.name}`} />
            {index < groupPath.length - 1 ? <span className={classes.breadcrumbText}>{">"}</span> : null}
          </React.Fragment>
        ))}
      </div>

      <Timeline align="left" className={classes.timeline}>
        {groupPath.map((node, index) => {
          const isCurrent = index === groupPath.length - 1;
          return (
            <TimelineItem key={`${node.level}-${node.code}`} className={classes.timelineItem}>
              <TimelineSeparator>
                <TimelineDot color={isCurrent ? "primary" : "grey"} variant={isCurrent ? "default" : "outlined"} />
                {index < groupPath.length - 1 ? <TimelineConnector className={classes.connector} /> : null}
              </TimelineSeparator>
              <TimelineContent className={classes.timelineContent}>
                <Typography className={classes.levelLabel}>LEVEL {node.level}</Typography>
                <Typography className={classes.levelCode}>{node.code}</Typography>
                <Typography className={classes.levelName}>{node.name}</Typography>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>

      <div className={classes.currentLevel}>
        <Typography variant="caption" color="textSecondary">
          Current Group
        </Typography>
        <Typography className={classes.levelCode}>{`Level ${currentGroup.level} | ${currentGroup.code}`}</Typography>
        <Typography className={classes.levelName}>{currentGroup.name}</Typography>
      </div>
    </Paper>
  );
};

export default AccountCodeGroupingPathMockup;
